/**
 * Vault list/update CONTRACT constants -- V-SCHEMA-FE.
 *
 * The two halves of the vault that talk to GET /vault/items and PUT /vault/items/{id}
 * share these so a token or a key is defined once, on the shape the server declares.
 */

/**
 * The default list order: newest first. `field:dir`, exactly what vaultApi.getVaultItems
 * splits into `sort_by` / `sort_order`.
 */
export const DEFAULT_VAULT_SORT = 'created_at:desc';

/**
 * Mobile sort menu. Every value is a REAL `field:dir` pair from the server's sort_by
 * allowlist ({created_at, updated_at, title, user_grade, purchase_date, purchase_price,
 * current_value, market_value, issue_number, publisher, year}) plus asc|desc.
 *
 * ⛔ The previous tokens (`added_desc`, `value_desc`, `grade_asc`, ...) were opaque
 * strings the server never declared. getVaultItems split them on ':' -- there was no
 * ':' -- and sent `sort_by=added_desc`, which the route resolved through
 * `getattr(UserCollection, sort_by, created_at)` to the fallback. Every mobile sort was
 * therefore created_at DESC while the menu highlighted whatever the user picked. With the
 * server now 422-ing unknown fields, an inert token would be a visible failure instead of
 * a silent one; a real pair is the only thing that works.
 */
export const VAULT_SORT_OPTIONS = Object.freeze([
  { value: 'created_at:desc', label: 'Recently Added' },
  { value: 'created_at:asc', label: 'Oldest First' },
  { value: 'title:asc', label: 'Title A-Z' },
  { value: 'title:desc', label: 'Title Z-A' },
  { value: 'user_grade:desc', label: 'Highest Grade' },
  { value: 'user_grade:asc', label: 'Lowest Grade' },
  { value: 'current_value:desc', label: 'Highest Value' },
]);

/**
 * Every key PUT /vault/items/{id} (CollectionItemUpdate, extra='forbid') accepts.
 *
 * `grade` and `current_value` are deliberately ABSENT: the schema 422s them. `grade` was
 * never a field (it is `user_grade`); `current_value` is OP-2 HELD -- it returns as either
 * a user override or a derived-only field once the operator decides, and until then no
 * client writes it.
 */
export const VAULT_ITEM_UPDATE_FIELDS = Object.freeze([
  'title',
  'issue_number',
  'publisher',
  'year',
  'comic_issue_id',
  'user_grade',
  'is_graded',
  'grading_company',
  'cert_number',
  'label_type',
  'grade_confidence',
  'is_signed',
  'signature_details',
  'purchase_price',
  'purchase_date',
  'purchase_location',
  'custom_tags',
  'notes',
  'grade_notes',
  'variant_description',
  'asking_price',
  'is_for_sale',
  'is_wanted',
  'is_read',
  'box_id',
]);

/**
 * Keep only the keys the update schema accepts.
 *
 * A forbid schema turns a stray key into a 422 for the WHOLE request, so one leftover
 * field in a form's editData would block saving the fields that are fine. Strip before
 * sending; the caller decides what to do about a key that was dropped.
 *
 * @param {Object} updates
 * @returns {{payload: Object, dropped: string[]}}
 */
export function pickVaultItemUpdate(updates) {
  const payload = {};
  const dropped = [];
  for (const key of Object.keys(updates ?? {})) {
    if (VAULT_ITEM_UPDATE_FIELDS.includes(key)) {
      payload[key] = updates[key];
    } else {
      dropped.push(key);
    }
  }
  return { payload, dropped };
}

export default {
  DEFAULT_VAULT_SORT,
  VAULT_SORT_OPTIONS,
  VAULT_ITEM_UPDATE_FIELDS,
  pickVaultItemUpdate,
};
